import React, { useState, useEffect } from 'react';
import { ChevronDown, Camera, Calendar, Users, Trophy, MapPin, Mail, Phone, Instagram, Play, ExternalLink } from 'lucide-react';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Sample portfolio images - replace with actual images
  const portfolioImages = [
    { src: '/api/placeholder/800/600', title: 'Rally Cross Action', category: 'rally' },
    { src: '/api/placeholder/800/600', title: 'Track Day Precision', category: 'track' },
    { src: '/api/placeholder/800/600', title: 'Car Culture Portrait', category: 'culture' },
    { src: '/api/placeholder/800/600', title: 'Racing Dynamics', category: 'racing' },
    { src: '/api/placeholder/800/600', title: 'Event Coverage', category: 'events' },
    { src: '/api/placeholder/800/600', title: 'Automotive Detail', category: 'detail' }
  ];

  const services = [
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Automotive Photography",
      description: "Professional car photography sessions showcasing your vehicle's best angles and details.",
      features: ["Studio & Location Shoots", "High-Resolution Images", "Professional Editing", "Quick Turnaround"]
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "Race Day Coverage",
      description: "Comprehensive racing event photography capturing the excitement and intensity of motorsports.",
      features: ["Live Action Shots", "Pit Lane Coverage", "Victory Lane Moments", "Team Photography"]
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Track Day Photography",
      description: "You focus on driving, we'll capture every thrilling moment on the track.",
      features: ["Multiple Track Positions", "Action & Candid Shots", "Same-Day Preview", "Digital Gallery Access"]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Car Events",
      description: "Complete event coverage for car shows, meets, and automotive gatherings.",
      features: ["Event Documentation", "Crowd & Vehicle Shots", "Social Media Ready", "Event Recap Videos"]
    }
  ];

  const projects = [
    {
      title: "Rally X Documentary",
      description: "Unveiling the Heart of Rally Cross - An intimate look into the world of rallycross racing.",
      image: "/api/placeholder/600/400",
      category: "Documentary",
      status: "Featured Project"
    },
    {
      title: "SoCal Car Culture",
      description: "What drives what we drive - Exploring Southern California's diverse automotive scene.",
      image: "/api/placeholder/600/400",
      category: "Photography Series",
      status: "Ongoing"
    },
    {
      title: "Track Day Chronicles",
      description: "Stopping motion 1 frame at a time - The intensity and precision of track day photography.",
      image: "/api/placeholder/600/400",
      category: "Action Photography",
      status: "Portfolio"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % portfolioImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMenuOpen(false);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Camera className="w-8 h-8 text-blue-500" />
              <span className="text-2xl font-bold">N3RD MEDIA</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              {['home', 'services', 'projects', 'portfolio', 'about', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`capitalize hover:text-blue-500 transition-colors ${
                    activeSection === item ? 'text-blue-500' : 'text-white'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="space-y-1">
                <div className="w-6 h-0.5 bg-white"></div>
                <div className="w-6 h-0.5 bg-white"></div>
                <div className="w-6 h-0.5 bg-white"></div>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['home', 'services', 'projects', 'portfolio', 'about', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block px-3 py-2 text-base font-medium capitalize hover:text-blue-500 transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
          style={{
            backgroundImage: `url(${portfolioImages[currentImageIndex]?.src})`,
          }}
        ></div>
        
        <div className="relative z-20 text-center max-w-4xl mx-auto px-4">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent">
            N3RD MEDIA
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-gray-300">
            We are passionate about the pixels
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto text-gray-400">
            Southern California's premier automotive photography specialists. Capturing the heart of car culture, racing excitement, and track day thrills.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection('services')}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
            >
              Explore Services
            </button>
            <button 
              onClick={() => scrollToSection('portfolio')}
              className="border border-gray-400 text-gray-400 hover:bg-gray-400 hover:text-black px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
            >
              View Portfolio
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Professional automotive photography services tailored to capture the essence of car culture and motorsports
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-black/50 p-8 rounded-xl hover:bg-black/70 transition-all duration-300 hover:transform hover:scale-105">
                <div className="text-red-500 mb-4">{service.icon}</div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-400 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-300 flex items-center">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg font-semibold transition-colors"
            >
              Book a Session
            </button>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section id="projects" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Featured Projects</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Showcasing our passion projects and documentary work in the automotive world
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 text-sm font-semibold">{project.category}</span>
                    <span className="text-gray-400 text-sm">{project.status}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-gray-300 text-sm">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section id="portfolio" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Portfolio</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A collection of our finest automotive photography work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolioImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-lg aspect-square">
                <img 
                  src={image.src} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-1">{image.title}</h3>
                    <p className="text-blue-400 text-sm capitalize">{image.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-8 py-4 rounded-lg font-semibold transition-colors">
              View Full Gallery
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">About N3RD Media</h2>
              <p className="text-xl text-gray-400 mb-6">
                Based in Southern California, we're passionate about capturing the automotive world through our lens.
              </p>
              <p className="text-gray-300 mb-6">
                As a father and husband working out of Southern California, I bring a passion for all things with wheels and engines. 
                An avid technology and security enthusiast as well as overall media professional, we enjoy capturing moments by freezing time with our camera.
              </p>
              <p className="text-gray-300 mb-8">
                Our mission is to capture and celebrate the beauty and passion of the automotive world through stunning photography 
                and engaging content that brings car enthusiasts together.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2 text-gray-400">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span>Southern California</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <Camera className="w-5 h-5 text-blue-500" />
                  <span>Professional Photography</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="/api/placeholder/600/400" 
                alt="Behind the scenes" 
                className="rounded-xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-blue-600 p-4 rounded-xl">
                <p className="text-white font-bold text-lg">We are passionate<br />about the pixels</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Get In Touch</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Ready to capture your automotive story? Let's discuss your photography needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Mail className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-gray-400">info@n3rd-media.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Phone className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p className="text-gray-400">Available upon request</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <MapPin className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-semibold">Location</p>
                    <p className="text-gray-400">Southern California</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  <button className="bg-gray-700 hover:bg-blue-600 p-3 rounded-lg transition-colors">
                    <Instagram className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 p-8 rounded-xl border border-gray-700">
              <h3 className="text-2xl font-bold mb-6">Book a Session</h3>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="Your Name"
                    className="bg-gray-700 border border-gray-600 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <input 
                    type="email" 
                    placeholder="Your Email"
                    className="bg-gray-700 border border-gray-600 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <select className="w-full bg-gray-700 border border-gray-600 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500">
                  <option>Select Service</option>
                  <option>Automotive Photography</option>
                  <option>Race Day Coverage</option>
                  <option>Track Day Photography</option>
                  <option>Car Events</option>
                </select>
                <textarea 
                  rows={4}
                  placeholder="Tell us about your project..."
                  className="w-full bg-gray-700 border border-gray-600 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500"
                ></textarea>
                <button 
                  onClick={() => alert('Contact form integration needed for production')}
                  className="w-full bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg font-semibold transition-colors"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <Camera className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold">N3RD MEDIA</span>
              </div>
              <p className="text-gray-400 mb-6">
                We are passionate about the pixels. Southern California's premier automotive photography specialists.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Automotive Photography</li>
                <li>Race Day Coverage</li>
                <li>Track Day Photography</li>
                <li>Car Events</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>About Us</li>
                <li>Portfolio</li>
                <li>Blog</li>
                <li>Contact</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 N3RD Media. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;